import { appApiRoutes } from '@riskbase/types/http/appApi';
import { isError } from 'lib/isError';
import { randomKey } from 'lib/riskbase';
import { requestRoute } from 'service/api';
import { Api } from 'service/api/constants';
import { getFileContentType } from 'service/file';
import {
  ALERT_ERROR,
  HIDE_BLOCKING_LOADER,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  SHOW_BLOCKING_LOADER,
} from 'store/actions';
import {
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
  takeLeading,
} from 'typed-redux-saga';
import { ModalScreens } from 'components/Navigation/types';

import {
  doTasksAssignedFilterOpenModal,
  doTasksAssignedFilterOptionsLoad,
  doTasksAssignedFilterOptionsLoadFailure,
  doTasksAssignedFilterOptionsLoadSuccess,
  doTaskAssignedDetailsLoadFailure,
  doTaskAssignedDetailsLoadSuccess,
  doTasksAssignedDetailsLoad,
  doTasksAssignedLoad,
  doTasksAssignedLoadFailure,
  doTasksAssignedLoadSuccess,
  doTasksAssignedSetFilter,
  doTaskAssignedCommentCreate,
  doTaskAssignedCommentCreateFailure,
  doTaskAssignedCommentCreateSuccess,
  doTaskAssignedCommentDraftAttachmentUpload,
  doTaskAssignedStatusSave,
  doTaskAssignedStatusSaveSuccess,
} from './actions';
import {
  selectTasksAssignedDraftFilterOptions,
  selectTasksAssignedFilter,
  selectTasksAssignedFilterOptionsLoadStatus,
  selectTaskDetailsState,
} from './selectors';
import { selectUser } from 'store/auth/selectors';
import {
  addFileUpload,
  failFileUpload,
  removeFileUpload,
  setFileUploadTarget,
} from 'store/fileUpload/actions';
import {
  selectFileUpload,
  selectFileUploads,
} from 'store/fileUpload/selectors';
import { sagaConfirm } from 'store/sagaHelpers';
import { pluralise } from 'lib/pluralise';
import { getTasksAssignedDefaultStatusIds } from 'types/Tasks';

function* loadTasks(action: ReturnType<typeof doTasksAssignedLoad>): Generator {
  const { filter: newFilter, showBlockingLoader, page } = action.payload;
  const hasNewFilter = !!newFilter;
  const currentFilter = yield* select(selectTasksAssignedFilter);
  const filter = { ...currentFilter, ...newFilter };

  if (showBlockingLoader) {
    yield* put(SHOW_BLOCKING_LOADER());
  }
  try {
    const response = yield* requestRoute({
      api: Api.APP,
      route: appApiRoutes.tasks.get,
      query: {
        organisations: filter.organisations,
        properties: filter.properties,
        search: filter.search || undefined,
        dueDateFrom: filter.dueDateFrom || undefined,
        dueDateTo: filter.dueDateTo || undefined,
        statuses: filter.statuses,
        page,
      },
    });

    // Put results into store
    yield* put(
      doTasksAssignedLoadSuccess({
        tasks: response.data,
        dueGroups: response.included.dueGroups,
        pagination: response.pagination,
        append: typeof page === 'number' && page > 1,
      }),
    );

    // Save filter if it's new
    if (hasNewFilter) {
      yield* put(doTasksAssignedSetFilter(filter));
    }

    // If draft filter options exist, save them as non-draft
    const draftFilterOptions = yield* select(
      selectTasksAssignedDraftFilterOptions,
    );
    if (draftFilterOptions) {
      yield* put(
        doTasksAssignedFilterOptionsLoadSuccess({
          filterOptions: draftFilterOptions,
        }),
      );
    }

    // Close filter modal (if it's open)
    yield* put(NAVIGATE_MODAL_BACK([ModalScreens.TasksAssignedFilter]));
  } catch (error) {
    if (isError(error)) {
      if (showBlockingLoader) {
        yield* put(ALERT_ERROR({ error }));
      } else {
        yield* put(
          doTasksAssignedLoadFailure({
            error: error.message,
          }),
        );
      }
    }
  }
  if (showBlockingLoader) {
    yield* put(HIDE_BLOCKING_LOADER());
  }
}

function* loadTaskFilterOptions(
  action: ReturnType<typeof doTasksAssignedFilterOptionsLoad>,
): Generator {
  // This is mimicking whats done in doAsesListFilter
  const blockingLoaderKey = action.payload.showBlockingLoader
    ? randomKey()
    : undefined;
  try {
    if (blockingLoaderKey) {
      yield* put(SHOW_BLOCKING_LOADER(blockingLoaderKey));
    }
    const { organisationIds, draft } = action.payload;
    const response = yield* requestRoute({
      api: Api.APP,
      route: appApiRoutes.tasks.getFilterOptions,
      query: organisationIds?.length ? { organisationIds } : {},
    });

    yield* put(
      doTasksAssignedFilterOptionsLoadSuccess({
        filterOptions: response,
        draft,
      }),
    );
    if (action.payload.loadTasksOnSuccess && !response.organisationRequired) {
      yield* put(
        doTasksAssignedLoad({
          filter: {
            statuses: getTasksAssignedDefaultStatusIds(response.statuses),
          },
        }),
      );
    }
  } catch (error) {
    const finalError = isError(error)
      ? error
      : new Error('Unable to load task filter options');

    yield* put(doTasksAssignedFilterOptionsLoadFailure({ error: finalError }));
    if (action.payload.showError) {
      yield* put(ALERT_ERROR({ error: finalError }));
    }
  } finally {
    if (blockingLoaderKey) {
      yield* put(HIDE_BLOCKING_LOADER(blockingLoaderKey));
    }
  }
}

function* openTaskFilterModal(): Generator {
  const filterOptionsLoadStatus = yield* select(
    selectTasksAssignedFilterOptionsLoadStatus,
  );

  if (filterOptionsLoadStatus !== 'loaded') {
    yield* put(SHOW_BLOCKING_LOADER());
    if (filterOptionsLoadStatus !== 'loading') {
      yield* put(doTasksAssignedFilterOptionsLoad({}));
    }
    const taken = yield* race({
      fail: take(doTasksAssignedFilterOptionsLoadFailure),
      done: take(doTasksAssignedFilterOptionsLoadSuccess),
    });
    yield* put(HIDE_BLOCKING_LOADER());
    if (taken.fail) {
      yield* put(ALERT_ERROR({ error: taken.fail.payload.error }));
      return;
    }
  }

  yield* put(
    NAVIGATE_MODAL({
      screen: ModalScreens.TasksAssignedFilter,
      size: 'small',
      params: {},
    }),
  );
}
function* loadTaskDetails(
  action: ReturnType<typeof doTasksAssignedDetailsLoad>,
): Generator {
  const { taskId } = action.payload;
  try {
    const response = yield* requestRoute({
      api: Api.APP,
      route: appApiRoutes.tasks.getDetails,
      params: { id: taskId },
    });

    yield* put(doTaskAssignedDetailsLoadSuccess({ task: response }));
  } catch (error) {
    yield* put(
      doTaskAssignedDetailsLoadFailure({
        error: isError(error) ? error.message : null,
      }),
    );
  }
}

function* saveTaskStatus(
  action: ReturnType<typeof doTaskAssignedStatusSave>,
): Generator {
  const { taskId, status } = action.payload;
  const taskDetails = yield* select(selectTaskDetailsState);
  const currentTask = taskDetails.item?.id === taskId ? taskDetails.item : null;
  const previousStatus = currentTask?.activeStatus;
  const user = yield* select(selectUser);
  try {
    yield* put(SHOW_BLOCKING_LOADER());
    yield* requestRoute({
      api: Api.APP,
      route: appApiRoutes.tasks.updateStatus,
      params: { taskId },
      body: {
        statusId: status.statusId,
      },
    });
    const statusChangeComment =
      previousStatus && previousStatus.statusId !== status.statusId && user
        ? {
            id: -Date.now(),
            comment: '',
            createdDate: new Date().toISOString(),
            author: {
              id: user.id,
              name: user.name,
              avatar: user.avatar || null,
            },
            files: [],
            statusChange: {
              from: previousStatus,
              to: status,
            },
          }
        : null;
    yield* put(
      doTaskAssignedStatusSaveSuccess({
        taskId,
        status,
        statusChangeComment,
      }),
    );
    yield* put(NAVIGATE_MODAL_BACK([ModalScreens.TasksAssignedStatus]));
    yield* put(doTasksAssignedLoad({}));
  } catch (error) {
    const finalError = isError(error)
      ? error
      : new Error('Unable to update task status');
    yield* put(ALERT_ERROR({ error: finalError }));
  } finally {
    yield* put(HIDE_BLOCKING_LOADER());
  }
}

function* createTaskComment(
  action: ReturnType<typeof doTaskAssignedCommentCreate>,
): Generator {
  const { comment, composerId, submissionId, taskId, uploadIds } =
    action.payload;
  const trimmedComment = comment.trim();
  const currentUploads = yield* select(selectFileUploads);
  const resolvedUploads = uploadIds.flatMap((uploadId) => {
    const upload = currentUploads.find((u) => u.uploadId === uploadId);
    return upload ? [upload] : [];
  });

  if (!trimmedComment) {
    yield* put(
      doTaskAssignedCommentCreateFailure({
        taskId,
        composerId,
        submissionId,
      }),
    );
    return;
  }

  const erroredFilesCount = resolvedUploads.filter(
    (upload) => upload.status === 'error',
  ).length;
  if (erroredFilesCount) {
    if (
      !(yield* sagaConfirm({
        title: `${pluralise(erroredFilesCount, 'File', '{x} Files')} Not Attached`,
        text:
          (resolvedUploads.length > 1
            ? `${pluralise(erroredFilesCount, '{x} file', '{x} files')} couldn’t be attached to the comment.`
            : 'Your file couldn’t be attached to the comment.') +
          ` Do you want to submit the comment without ${pluralise(erroredFilesCount, 'it', 'them')}?`,
        confirmText: 'Submit Comment',
      }))
    ) {
      yield* put(
        doTaskAssignedCommentCreateFailure({
          taskId,
          composerId,
          submissionId,
        }),
      );
      return;
    }
  }

  const waitingFilesCount = resolvedUploads.filter(
    (upload) =>
      upload.status === 'preparing' ||
      upload.status === 'queued' ||
      upload.status === 'uploading',
  ).length;
  if (waitingFilesCount) {
    yield* put(
      ALERT_ERROR({
        error: new Error(
          waitingFilesCount === 1
            ? 'Your attachment is still uploading. You’ll be able to send your comment when its finished.'
            : 'Your attachments are still uploading. You’ll be able to send your comment when they’re finished.',
        ),
      }),
    );
    yield* put(
      doTaskAssignedCommentCreateFailure({
        taskId,
        composerId,
        submissionId,
      }),
    );
    return;
  }

  const files = resolvedUploads.flatMap((upload) =>
    upload.status === 'uploaded' && upload.remoteId
      ? [{ id: upload.remoteId, filename: upload.filename }]
      : [],
  );

  try {
    const response = yield* requestRoute({
      api: Api.APP,
      route: appApiRoutes.tasks.createComment,
      params: { taskId },
      body: {
        comment: trimmedComment,
        files: files.length ? files : undefined,
      },
    });

    // Use the response as the store comment
    yield* put(
      doTaskAssignedCommentCreateSuccess({
        taskId,
        composerId,
        submissionId,
        comment: response,
      }),
    );
    for (const upload of resolvedUploads) {
      yield* put(removeFileUpload({ uploadId: upload.uploadId }));
    }
  } catch (error) {
    yield* put(
      doTaskAssignedCommentCreateFailure({
        taskId,
        composerId,
        submissionId,
      }),
    );
    yield* put(
      ALERT_ERROR({
        error: isError(error)
          ? error
          : new Error('Unable to post task comment'),
      }),
    );
  }
}

function* prepareTaskCommentDraftAttachment(
  action: ReturnType<typeof doTaskAssignedCommentDraftAttachmentUpload>,
): Generator {
  const { file, uploadId } = action.payload;
  const contentType = getFileContentType(file);
  // Web: file.file IOS/Android: file.uri
  const uploadFile = file.file ?? { uri: file.uri };
  const totalBytes = file.size || file.file?.size || 1;

  const existingUpload = yield* select(selectFileUpload, uploadId);
  if (existingUpload) {
    return;
  }

  yield* put(
    addFileUpload({
      uploadId,
      file: uploadFile,
      filename: file.name,
      contentType,
      totalBytes,
    }),
  );
  const addedUpload = yield* select(selectFileUpload, uploadId);
  if (!addedUpload || addedUpload.file !== uploadFile) {
    return;
  }

  try {
    const response = yield* requestRoute({
      api: Api.APP,
      route: appApiRoutes.tasks.getTaskCommentFileUploadUrl,
      body: {
        mimeType: contentType,
      },
    });
    if (!response.url) {
      throw new Error('Unable to prepare draft attachment');
    }
    yield* put(
      setFileUploadTarget({
        uploadId,
        target: {
          url: response.url,
          remoteId: response.id,
          method: 'PUT',
        },
      }),
    );
  } catch (error) {
    const finalError = isError(error)
      ? error
      : new Error('Unable to prepare draft attachment');
    yield* put(
      failFileUpload({
        uploadId,
        error: finalError.message,
      }),
    );
  }
}

export const tasksSideEffects = [
  takeLatest(doTasksAssignedLoad, loadTasks),
  takeLatest(doTasksAssignedDetailsLoad, loadTaskDetails),
  takeLatest(doTaskAssignedStatusSave, saveTaskStatus),
  takeEvery(doTaskAssignedCommentCreate, createTaskComment),
  takeEvery(
    doTaskAssignedCommentDraftAttachmentUpload,
    prepareTaskCommentDraftAttachment,
  ),
  takeLatest(doTasksAssignedFilterOptionsLoad, loadTaskFilterOptions),
  takeLeading(doTasksAssignedFilterOpenModal, openTaskFilterModal),
];
